import { DateDatum, IndexDatum, FixedDatum, TimeSeries, FixedSeries, DateSeries, IndexSeries } from './DataSeries';

const _ = require('lodash');


/**
 * Ensure input is a list by wrapping a non-list input or returning the input list
 * 
 * @param {Any} input 
 */
export function makeArray(input) {
  if (input instanceof TimeSeries || input instanceof FixedSeries) {
    return [input];
  } else if (!Array.isArray(input)) {
    return [input];
  } else {
    return input;
  }
}


/**
 * Make an object out of ORDERED arrays of keys and values
 * 
 * @param {String[]} keys Array of keys to store
 * @param {Any[]} values Array of values to store
 */
export function makeObject(keys, values) {
  let output = {};
  values.map((v,i) => { output[keys[i]] = v });
  return output;
}


/**
 * Check if the provided array is a subset of the supersetArray of values
 * 
 * @param {Array} anArray A random array to check
 * @param {Array} supersetArray Superset array of values to check against
 */
export function isSubset(anArray, supersetArray) {
  return _.difference(anArray, supersetArray).length === 0;
}


/**
 * Safely convert an input string to JSON returning an empty object
 * if conversion fails (or the input if already an object)
 * 
 * @param {String|JSON} input Input string to convert to JSON
 */
export function convertToJSON(input) {
  try {
    if (typeof input === 'object') return input;
    else return JSON.parse(input);
  } catch(err) {
    console.log('Error parsing string into JSON');
    return {};
  }
}


/**
 * Check that the value matches the expected type and does not contain
 * any obviously suspicious values (does not validate security but 
 * reduces risk of clearly suspicious values)
 * 
 * @param {Any} value Value to check the type against
 * @param {String} typeString String name of the type to check
 */
export function checkValueType(value, typeString) {
  switch(typeString) {
    case 'string': {
      let valid = (typeof value === 'string');
      for (let str of ['function','()','import','=>','script','src','http']) {
        valid = valid && !value.includes(str);
      }
      return valid;
    }
    case 'boolean': {
      return typeof value === 'boolean';
    }
    case 'number': {
      return typeof value === 'number';
    }
    case 'array:number': {
      return Array.isArray(value) && value.every(v => typeof v === 'number');
    }
    default: {
      return false;
    }
  }
}


/**
 * Convert an unknown input into a Datum type
 * 
 * @param {Array|DateDatum|IndexDatum|Number} input Input to convert and store in the array
 */
export function convertToDatum(input) {
  if (input instanceof DateDatum || input instanceof IndexDatum || input instanceof FixedDatum) {
    return input;
  } else if (Array.isArray(input) && input.length === 2 && input[0] instanceof Date) {
    return new DateDatum(input[0], input[1]);
  } else if (Array.isArray(input) && input.length === 2 && typeof input[0] === 'number') {
    return new IndexDatum(input[0], input[1]);
  } else if (!Array.isArray(input) && typeof input !== 'string') {
      return new FixedDatum(input);
  } else {
    throw Error('Datum type of input cannot be determined');
  }
}


/**
 * Convert an unknown input to a Series
 * 
 * @param {DateDatum[]|IndexDatum[]|Number} input Input to convert and store in the array
 */
export function convertToSeries(input) {
  let firstElement = Array.isArray(input) ? input[0] : input;
  let firstDatum = convertToDatum(firstElement);

  if (firstDatum instanceof FixedDatum) {
    return new FixedSeries(input);
  } else if (firstDatum instanceof DateDatum) {
    return new DateSeries(input);
  } else if (firstDatum instanceof IndexDatum) {
    return new IndexSeries(input);
  } else {
    throw Error('Series type of input cannot be determined');
  }
}


/**
 * Merges the provided TimeSeries so ALL dates are present and
 * all values, for each date, are combined into an array
 * 
 * @example
 * Input:
 *  -> a: [2015, 1], [2016, 2]
 *  -> b: [2015, 3],            [2018, 4]
 * 
 * Output:
 *  -> [2015, [1,3]], [2016, [2,null]], [2018, [null,4]]
 * 
 * @param  {...TimeSeries} ts Set of TimeSeries to merge
 */
export function mergeTimeSeries(...series) {
  let newData = [];

  // let fixedSeries = series.filter(s => s instanceof FixedSeries);
  let timeSeries = series.filter(s => s instanceof TimeSeries);

  let indices = timeSeries.map(d => d.getIndices());
  let counters = timeSeries.map(_ => 0);

  // Make merge flexible for Date and non-Date indices
  let accessor = i => (indices[0][0] instanceof Date ? i.getTime() : i);

  // Create a sorted list of all the unique dates
  let uniqueIndices = _.unionBy(...indices, accessor);
  uniqueIndices = _.sortBy(uniqueIndices, accessor);

  for (let t=0; t < uniqueIndices.length; t++) {
    let time = accessor(uniqueIndices[t]);
    
    let vals = series.map((d,i) => {
      if (d instanceof TimeSeries) {
        let curDatum = d[counters[i]];
        if (curDatum && accessor(curDatum.index) === time) {
          // If current index is at right time, return value and increment index
          counters[i]++;
          return curDatum.value;
        } else { 
          // Otherwise, keep current index and return null value
          return null;
        }
      } else if (d instanceof FixedSeries) {
        return d.value;
      }      
    });

    newData.push([uniqueIndices[t], vals]);
  }
  return new TimeSeries(newData);
}